/**
 * COUCHE DE RÉSOLUTION DU TUNNEL DE PAIEMENT PAR PRODUIT (Lot 2 BED, GAP-2).
 *
 * Pendant crm-4l du resolver site-4l `src/lib/sogecommerce/shops.ts`. Là où
 * site-4l résout les credentials de la boutique Sogecommerce, crm-4l résout la
 * base URL du tunnel de paiement (le domaine sur lequel le lien BED pointe).
 *
 * Décision d'architecture ARRÊTÉE (§6.3 CDC) : boutique/tunnel UNIQUE partagé,
 * distinction produit par le CODE PRODUIT (`products.code` : `4lt`, `rdt`…).
 * Cette couche DÉFAUTE au tunnel partagé (4L Trophy) pour tout produit sans
 * tunnel dédié. Passer un produit (ex. Rose Trip, code `rdt`) sur son propre
 * tunnel = ajouter UNE entrée dans PRODUCT_BASE_URLS, jamais une réarchitecture.
 *
 * ⚠️ REVIEW VICTOR CRM #144 finding #3 — CORRIGÉ : l'implémentation précédente
 * routait sur `orderId.split('-')[0]`. Or le vrai format `order_id` Sandra est
 * `9274L1455` (SANS tiret), le BED n'ajoutant que `-BED` -> le « préfixe »
 * extrait était l'order_id entier, JAMAIS un code produit. Un tunnel dédié
 * n'aurait donc jamais été routé. On route désormais sur le CODE PRODUIT réel
 * du dossier (`products.code`), remonté serveur-side avec le payment_case
 * (RPC create_bed_payment_case.product_code ; usePaymentCase.product_code).
 *
 * Contrainte Vite : `import.meta.env` n'accepte que des clés STATIQUES (Vite les
 * remplace au build). On ne peut donc pas faire `import.meta.env[dynamicKey]` :
 * chaque entrée de la map nomme explicitement sa variable d'env. C'est le prix
 * du « joint de config » côté SPA — une ligne, pas une réarchitecture.
 */

// Base du tunnel partagé (4L Trophy). Défaut pour tout produit sans tunnel dédié.
// Aligne le fallback historique de useBedActions.
const SHARED_BASE_URL =
  (import.meta.env.VITE_PAYMENT_BASE_URL as string | undefined)?.trim() ||
  'https://www.4ltrophy.com';

// Map CODE PRODUIT (products.code, comparé en UPPER) → base du tunnel dédié.
// VIDE aujourd'hui : tous les produits connus (4L Trophy, code `4lt`) héritent
// du tunnel partagé.
//
// Le jour où Rose Trip a son propre tunnel, décommenter (et définir la var
// d'env `VITE_PAYMENT_BASE_URL_RDT`) :
//   RDT: (import.meta.env.VITE_PAYMENT_BASE_URL_RDT as string | undefined)?.trim(),
const PRODUCT_BASE_URLS: Record<string, string | undefined> = {};

/**
 * Fonction PURE de routage (testable sans le module env). Résout la base URL du
 * tunnel depuis un CODE PRODUIT (`products.code`, ex `4lt`/`rdt`), une map
 * code→URL et l'URL partagée par défaut. Comparaison insensible à la casse.
 *
 * COMPORTEMENT FAIL-OPEN ASSUMÉ (review Victor #6) : un code produit absent,
 * inconnu, ou dont l'entrée de tunnel est vide/blanche RETOURNE le tunnel
 * PARTAGÉ (`sharedUrl`) — ce n'est PAS un fail-closed. C'est voulu : le CDC
 * §6.3 fait du tunnel partagé le défaut légitime de tout produit sans tunnel
 * dédié. Aucune promesse de blocage : un produit mal configuré paie sur le
 * tunnel partagé, il n'est jamais rejeté.
 */
export function resolveBaseUrl(
  productCode: string | undefined | null,
  sites: Record<string, string | undefined>,
  sharedUrl: string
): string {
  const code = (productCode ?? '').trim().toUpperCase();
  const override = code ? sites[code]?.trim() : undefined;
  // override || sharedUrl : fail-OPEN vers le tunnel partagé (cf. docstring).
  return override || sharedUrl;
}

/**
 * Résout la base URL du tunnel de paiement pour un CODE PRODUIT donné, en
 * s'appuyant sur la config module (PRODUCT_BASE_URLS + SHARED_BASE_URL). Un
 * code produit absent ou inconnu → tunnel partagé (fail-open assumé, cf.
 * resolveBaseUrl).
 */
export function resolvePaymentBaseUrl(productCode?: string | null): string {
  return resolveBaseUrl(productCode, PRODUCT_BASE_URLS, SHARED_BASE_URL);
}
